import React from 'react';

import { idempotencyHeader } from 'Helpers/apiUtils';
import { formatDate } from 'Helpers/dateUtils';
import { formatAndRoundMoney, formatFigure } from 'Helpers/formatService';
import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

/**
 * Saved Drafts table for the EAs and DCs tab (IB-610 finding #2 — a held draft IS an EA/DC, so it
 * surfaces above Pending there rather than on the Plan tab, keeping a single source of truth).
 *
 * Lists durable saved drafts (DC + EA) for the event. Each row offers Resume (navigate to the
 * backend-provided resume_url, which auto-resumes the draft) and Delete (confirm → DELETE →
 * refresh). The section hides itself entirely when there are no saved drafts.
 *
 * `deleteDraftUrl` may arrive already plan-resolved (EAs and DCs tab) or carrying a
 * PROCUREMENT_PLAN_ID placeholder + a planId prop (legacy callers); the planId replace is a
 * no-op when the placeholder is absent, so both forms work.
 *
 * @ticket IB-610
 */
export default function SavedDraftsTable(props) {
    const { draftsIndexUrl, deleteDraftUrl, onChanged } = props;
    const toast = useToast();

    const draftsRequest = useQuery({
        enabled: !!draftsIndexUrl,
        queryFn: () => apiClient.get(draftsIndexUrl),
    });

    const drafts = draftsRequest.data?.data ?? [];

    const resumeDraft = (draft) => {
        if (draft.resume_url) {
            window.location.href = draft.resume_url;
        }
    };

    const deleteDraft = (draft) => {
        const confirmed = window.confirm($t('Are you sure you want to delete this draft?'));
        if (!confirmed) {
            return;
        }

        // Delete the specific draft group by id (not the item) so a coexisting DC+EA draft on the
        // same item — or an EA draft with no item_id — is deleted correctly (IB-610). The planId
        // replace is a no-op when deleteDraftUrl is already plan-resolved (EAs and DCs tab).
        const url = deleteDraftUrl.replace('PROCUREMENT_PLAN_ID', props.planId ?? '').replace('GROUP_ID', draft.id);

        apiClient
            .delete(url, { headers: { ...idempotencyHeader() } })
            .then(() => {
                toast.success($t('Draft deleted successfully'));
                draftsRequest.refetch();
                onChanged && onChanged();
            })
            .catch(() => {
                toast.error($t('Error deleting draft'));
            });
    };

    // Hide the whole section when there are no saved drafts (and not still loading).
    if (!draftsRequest.loading && drafts.length === 0) {
        return null;
    }

    return (
        <div className="tw-mb-12">
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                <h4 className="table__header">{$t('Saved Drafts')}</h4>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th>{$t('Name')}</th>
                            <th>{$t('Type')}</th>
                            <th>{$t('Value')}</th>
                            <th>{$t('Supplier')}</th>
                            <th>{$t('Saved')}</th>
                            <th>{$t('Status')}</th>
                            <th>{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drafts.map((draft) => (
                            <tr key={draft.id}>
                                <td>{draft.item_name}</td>
                                <td>{draft.type === 'ea' ? $t('EA') : $t('DC')}</td>
                                <td>{formatFigure(draft.value, formatAndRoundMoney)}</td>
                                <td>
                                    {draft.awaiting_supplier ? (
                                        <span className="tw-inline-flex tw-items-center tw-rounded tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-amber-800 tw-border tw-border-amber-400">
                                            {$t('Awaiting supplier')}
                                        </span>
                                    ) : (
                                        draft.supplier_name || '—'
                                    )}
                                </td>
                                <td>{draft.saved_at ? formatDate(draft.saved_at) : ''}</td>
                                <td>{$t('Draft')}</td>
                                <td>
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        <Button variant="primary" onClick={() => resumeDraft(draft)} disabled={!draft.resume_url}>
                                            {$t('Resume')}
                                        </Button>
                                        <TextButton variant="danger" onClick={() => deleteDraft(draft)}>
                                            {$t('Delete')}
                                        </TextButton>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
